import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageHeader, SectionLabel, Card, Stat, SampleBanner, StatusPill, Sparkline, Row } from '../components/ui.js';

interface CustomerSummary {
  account_id: string;
  display_name: string;
  vertical: string;
  city: string;
  state: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  plan: 'starter' | 'pro' | 'elite';
  mrr_cents: number;
  primary_owner_email: string;
  created_at: string;
  health_score: number;
  bloom_score: number;
  bloom_score_trend: number[];
  posts_30d: number;
  rank_movers_30d: number;
  open_audits: number;
  last_qa_status: 'pass' | 'warn' | 'fail';
  last_backup_at: string;
  last_backup_status: 'ok' | 'failed';
  internal_notes: { id: string; author: string; date: string; body: string }[];
  tags: string[];
  csm_owner: string;
  next_qbr: string;
}

const CUSTOMERS: Record<string, CustomerSummary> = {
  'glisten-gilbert': {
    account_id: 'glisten-gilbert',
    display_name: 'Glisten Gilbert',
    vertical: 'dental',
    city: 'Gilbert',
    state: 'AZ',
    status: 'active',
    plan: 'pro',
    mrr_cents: 64900,
    primary_owner_email: 'revan@glistendental.com',
    created_at: '2025-11-22T18:00:00Z',
    health_score: 92,
    bloom_score: 78,
    bloom_score_trend: [62, 64, 65, 67, 68, 69, 70, 71, 72, 73, 74, 75, 75, 76, 76, 77, 77, 77, 78, 78, 78, 78, 78, 78, 78, 78, 78, 78, 78, 78],
    posts_30d: 4,
    rank_movers_30d: 18,
    open_audits: 2,
    last_qa_status: 'pass',
    last_backup_at: '2026-05-05T09:02:00Z',
    last_backup_status: 'ok',
    internal_notes: [
      { id: 'n-1', author: 'Anas', date: '2026-05-04T18:30:00Z', body: 'Dr. Dawood asked about adding Spanish-language posts. Tabling until B9 ships.' },
      { id: 'n-2', author: 'Anas', date: '2026-04-30T12:00:00Z', body: 'Implants pillar missing 4 cluster links — auto-fix queued, will land in next daily run.' },
      { id: 'n-3', author: 'Anas', date: '2026-04-22T16:00:00Z', body: 'GBP claim verified. All 3 locations now showing in operator console.' },
    ],
    tags: ['founding', 'multi-location', 'dental', 'family-business'],
    csm_owner: 'Anas',
    next_qbr: '2026-06-15',
  },
  'glisten-mesa': {
    account_id: 'glisten-mesa',
    display_name: 'Glisten Mesa',
    vertical: 'dental',
    city: 'Mesa',
    state: 'AZ',
    status: 'active',
    plan: 'pro',
    mrr_cents: 64900,
    primary_owner_email: 'revan@glistendental.com',
    created_at: '2025-11-22T18:00:00Z',
    health_score: 89,
    bloom_score: 81,
    bloom_score_trend: [64, 66, 67, 69, 70, 71, 72, 73, 74, 75, 76, 76, 77, 78, 78, 79, 79, 80, 80, 80, 81, 81, 81, 81, 81, 81, 81, 81, 81, 81],
    posts_30d: 4,
    rank_movers_30d: 14,
    open_audits: 1,
    last_qa_status: 'pass',
    last_backup_at: '2026-05-05T09:04:00Z',
    last_backup_status: 'ok',
    internal_notes: [
      { id: 'n-1', author: 'Anas', date: '2026-05-02T14:00:00Z', body: 'AHCCCS coverage page expansion shipped — saw immediate +7 spot bump on "ahcccs dentist mesa". Bloom\'s pick to expand was right.' },
      { id: 'n-2', author: 'Anas', date: '2026-04-26T10:30:00Z', body: 'Pediatric content block landing well; primary contact = Filza for ops questions.' },
    ],
    tags: ['founding', 'multi-location', 'dental', 'family-business'],
    csm_owner: 'Anas',
    next_qbr: '2026-06-15',
  },
  'glisten-glendale': {
    account_id: 'glisten-glendale',
    display_name: 'Glisten Glendale',
    vertical: 'dental',
    city: 'Glendale',
    state: 'AZ',
    status: 'active',
    plan: 'pro',
    mrr_cents: 64900,
    primary_owner_email: 'revan@glistendental.com',
    created_at: '2025-11-22T18:00:00Z',
    health_score: 78,
    bloom_score: 71,
    bloom_score_trend: [60, 61, 63, 64, 64, 65, 65, 66, 67, 67, 68, 68, 69, 69, 69, 70, 70, 70, 70, 71, 71, 71, 71, 71, 71, 71, 71, 71, 71, 71],
    posts_30d: 4,
    rank_movers_30d: 9,
    open_audits: 3,
    last_qa_status: 'warn',
    last_backup_at: '2026-05-05T09:06:00Z',
    last_backup_status: 'ok',
    internal_notes: [
      { id: 'n-1', author: 'Anas', date: '2026-05-03T11:00:00Z', body: 'Cosmetic-dentist keyword dropped 2 spots — Bloom flagged + drafted recovery content. Awaiting Dr. Dawood approval.' },
      { id: 'n-2', author: 'Anas', date: '2026-04-28T15:00:00Z', body: 'Front-desk turnaround on review replies surfaced in audit. Already escalated to office manager.' },
    ],
    tags: ['founding', 'multi-location', 'dental', 'family-business', 'attention'],
    csm_owner: 'Anas',
    next_qbr: '2026-06-15',
  },
};

export function OperatorCustomerDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const customer = CUSTOMERS[accountId ?? ''] ?? null;
  const [noteDraft, setNoteDraft] = useState('');

  if (!customer) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Operator" title="Customer not found" />
        <Link to="/operator/customers" className="text-sm text-cream/55 hover:text-cream">← Back to all customers</Link>
        <p className="text-sm text-cream/55">
          Try one of: <Link to="/operator/customers/glisten-gilbert" className="text-cream/85 hover:text-cream">glisten-gilbert</Link>,{' '}
          <Link to="/operator/customers/glisten-mesa" className="text-cream/85 hover:text-cream">glisten-mesa</Link>,{' '}
          <Link to="/operator/customers/glisten-glendale" className="text-cream/85 hover:text-cream">glisten-glendale</Link>
        </p>
      </div>
    );
  }

  const mrr = (customer.mrr_cents / 100).toLocaleString();

  return (
    <div className="space-y-12">
      <div>
        <Link to="/operator/customers" className="text-[11px] uppercase tracking-[0.2em] text-cream/40 hover:text-cream/85 transition-colors">
          ← All customers
        </Link>
      </div>

      <PageHeader
        eyebrow={`${customer.city}, ${customer.state} · ${customer.vertical}`}
        title={customer.display_name}
        subtitle={`Bloom ${customer.plan} · $${mrr}/mo · onboarded ${new Date(customer.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        action={
          <div className="flex items-center gap-2">
            <StatusPill tone={customer.status === 'active' ? 'ok' : customer.status === 'trialing' ? 'info' : 'bad'}>{customer.status}</StatusPill>
            <StatusPill tone="info">{customer.plan}</StatusPill>
          </div>
        }
      />
      <SampleBanner note="Sample customer detail. Mirrors what the customer sees plus the internal CRM panel below." />

      <section>
        <SectionLabel>Health snapshot</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Health score" value={customer.health_score} hint="0-100, ops-internal" tone={customer.health_score >= 85 ? 'ok' : customer.health_score >= 70 ? 'warn' : 'bad'} />
          <Stat label="Bloom Score" value={customer.bloom_score} hint="0-100, customer-facing" tone={customer.bloom_score >= 75 ? 'ok' : customer.bloom_score >= 60 ? 'warn' : 'bad'} />
          <Stat label="Posts / 30d" value={customer.posts_30d} hint="target 4" tone={customer.posts_30d >= 4 ? 'ok' : 'warn'} />
          <Stat label="Open audits" value={customer.open_audits} tone={customer.open_audits === 0 ? 'ok' : customer.open_audits > 2 ? 'warn' : 'neutral'} />
        </div>
      </section>

      <section>
        <SectionLabel>Bloom Score (30d)</SectionLabel>
        <Card>
          <Sparkline values={customer.bloom_score_trend} height={64} color="rgba(165, 243, 252, 0.7)" />
          <div className="mt-3 flex items-center justify-between text-[11px] text-cream/45 tabular-nums">
            <span>30d ago: {customer.bloom_score_trend[0]}</span>
            <span>today: {customer.bloom_score_trend[customer.bloom_score_trend.length - 1]} ({customer.bloom_score_trend[customer.bloom_score_trend.length - 1]! - customer.bloom_score_trend[0]! > 0 ? '+' : ''}{customer.bloom_score_trend[customer.bloom_score_trend.length - 1]! - customer.bloom_score_trend[0]!})</span>
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Recent activity</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-2">Last QA</div>
            <div className="flex items-center gap-3">
              <StatusPill tone={customer.last_qa_status === 'pass' ? 'ok' : customer.last_qa_status === 'warn' ? 'warn' : 'bad'}>{customer.last_qa_status}</StatusPill>
              <span className="text-[11px] text-cream/55">5:00 AM today</span>
            </div>
          </Card>
          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-2">Last backup</div>
            <div className="flex items-center gap-3">
              <StatusPill tone={customer.last_backup_status === 'ok' ? 'ok' : 'bad'}>{customer.last_backup_status}</StatusPill>
              <span className="text-[11px] text-cream/55">{new Date(customer.last_backup_at).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', month: 'short', day: 'numeric' })}</span>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <SectionLabel>Internal CRM</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <Card>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Notes ({customer.internal_notes.length})</div>
              </div>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Add a note (visible to operators only)…"
                rows={2}
                className="w-full bg-cream/[0.02] border border-cream/[0.1] rounded-lg px-3 py-2 text-sm text-cream/90 placeholder:text-cream/30 focus:outline-none focus:border-cream/30"
              />
              <div className="flex justify-end mt-2">
                <button type="button" disabled={!noteDraft} className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-cream/[0.08] text-cream hover:bg-cream/[0.14] border border-cream/[0.16] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  Save note
                </button>
              </div>
              <div className="mt-4 space-y-3">
                {customer.internal_notes.map((n) => (
                  <div key={n.id} className="border-t border-cream/[0.06] pt-3 first:border-0 first:pt-0">
                    <div className="flex items-center gap-2 text-[11px] mb-1">
                      <span className="text-cream/85">{n.author}</span>
                      <span className="text-cream/40">·</span>
                      <span className="text-cream/40">{new Date(n.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <p className="text-sm text-cream/75 leading-relaxed">{n.body}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="space-y-3">
            <Card>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">Owner</div>
              <Row label="Email">{customer.primary_owner_email}</Row>
              <div className="mt-3"><Row label="CSM">{customer.csm_owner}</Row></div>
              <div className="mt-3"><Row label="Next QBR">{new Date(customer.next_qbr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Row></div>
            </Card>
            <Card>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">Tags</div>
              <div className="flex flex-wrap gap-2">
                {customer.tags.map((t) => (
                  <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-cream/[0.06] text-cream/70 border border-cream/[0.12]">
                    {t}
                  </span>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
